import { PackageCheck, X } from 'lucide-react';
import type { BackorderRow, BackorderStatus } from '@eombor/contracts';
import { backorderStatusLabel } from '@eombor/contracts';
import { Badge, Button, Skeleton, Table, cn, type BadgeTone, type Column } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { DESKTOP_QUERY, useMediaQuery } from '../../../shared/ui/useMediaQuery.js';
import { BackorderCard } from './BackorderCard.js';
import { PickBox } from './PickBox.js';
import type { BackorderSelection } from '../model/selection.js';

/**
 * BILDIRGILAR RO'YXATI — keng ekranda JADVAL, telefonda kartochka.
 *
 * Ilgari hamma yerda kartochka edi va har biri to'rt-besh qator joy
 * egallardi: maqsad matni, so'ragan odam, sana, rad sababi. O'n-o'n
 * beshta bildirgi kelganda ro'yxat ekranga sig'may ketardi va skladchi
 * «qaysi biri yangi» degan savolga g'ildirakni aylantirib javob
 * izlardi — talabnomalar ro'yxatida esa xuddi shu savol bir qarashda
 * hal bo'ladi.
 *
 * Telefonda jadval qolmaydi: yettita ustun 390px ga sig'maydi va
 * gorizontal siljish ro'yxatni kartochkadan ham battar qiladi.
 */

const TONES: Record<BackorderStatus, BadgeTone> = {
  SUBMITTED: 'info',
  ACCEPTED: 'warn',
  FULFILLED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
};

/** Miqdorni uch xonagacha yaxlitlaydi — ustun `numeric(14,3)`. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

const dateText = (iso: string | null): string | null => {
  if (iso === null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};

export interface BackorderTableProps {
  rows: readonly BackorderRow[];
  caption: string;
  pending?: boolean;
  /** Belgilash — faqat «Kutilmoqda» bo'limida beriladi. */
  selection?: BackorderSelection;
  /** Qatorga tushadigan amallar — qaysi biri ochiqligini chaqiruvchi hal qiladi. */
  actionsFor: (row: BackorderRow) => {
    onAccept?: (row: BackorderRow) => void;
    onFulfil?: (row: BackorderRow) => void;
    onCancel?: (row: BackorderRow) => void;
  };
}

export const BackorderTable = ({
  rows,
  caption,
  pending,
  actionsFor,
  selection,
}: BackorderTableProps) => {
  const desktop = useMediaQuery(DESKTOP_QUERY);

  if (pending === true) {
    return desktop ? (
      <Skeleton className="h-64 w-full" />
    ) : (
      <div className="flex flex-col gap-2.5">
        {[0, 1, 2].map((key) => (
          <Skeleton key={key} className="h-32 w-full rounded-[var(--radius-lg)]" />
        ))}
      </div>
    );
  }

  if (!desktop) {
    return (
      <ul aria-label={caption} className="flex flex-col gap-3">
        {rows.map((row) => (
          <li key={row.id}>
            <BackorderCard row={row} selection={selection} {...actionsFor(row)} />
          </li>
        ))}
      </ul>
    );
  }

  const columns: Column<BackorderRow>[] = [
    /* Katakcha ENG chapda: ko'z ro'yxatni shu ustundan boshlab kezadi
       va belgilash tugmadan oldin keladi. */
    ...(selection === undefined
      ? []
      : [
          {
            key: 'pick',
            header: '',
            width: '2.5rem',
            render: (row: BackorderRow) => <PickBox row={row} selection={selection} />,
          } satisfies Column<BackorderRow>,
        ]),
    { key: 'docNo', header: t('backorder.colDocNo'), mono: true, width: '10rem' },
    {
      key: 'status',
      header: t('backorder.colStatus'),
      width: '9rem',
      render: (row) => <Badge tone={TONES[row.status]}>{backorderStatusLabel(row.status)}</Badge>,
    },
    {
      /*
       * Mahsulot — ro'yxatdagi ENG muhim ustun: skladchi «nima
       * so'ralgan» bo'yicha ko'zdan kechiradi, hujjat raqami bo'yicha
       * emas. Katalogga bog'lanmagani qiya yozuvda: uni qabul qilish
       * qo'shimcha qadam talab qiladi va buni bir qarashda ko'rish kerak.
       */
      key: 'nomenclatureName',
      header: t('backorder.colProduct'),
      render: (row) => (
        <span className="flex max-w-[26rem] items-center gap-2">
          <span className={cn('min-w-0 truncate', row.nomenclatureId === null && 'italic')}>
            {row.nomenclatureName ?? row.freeText}
          </span>
          {/* «Katalogda yo'q» belgisi jadvalda ham QOLADI: usiz skladchi
              oddiy bildirgi deb o'ylab, biriktirishda katalogdan mahsulot
              tanlash kerakligini faqat oyna ochilganda bilardi. */}
          {row.nomenclatureId === null ? (
            <Badge tone="warn" className="shrink-0">
              {t('backorder.unbound')}
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      key: 'requestedQty',
      header: t('backorder.colQty'),
      align: 'right',
      mono: true,
      width: '8rem',
      render: (row) => `${qtyText(row.requestedQty)} ${row.unit ?? ''}`.trim(),
    },
    {
      key: 'requesterName',
      header: t('backorder.colRequester'),
      width: '13rem',
      render: (row) => <span className="block truncate">{row.requesterName}</span>,
    },
    {
      key: 'warehouseName',
      header: t('backorder.colWarehouse'),
      width: '13rem',
      render: (row) => <span className="block truncate">{row.warehouseName}</span>,
    },
    {
      key: 'neededBy',
      header: t('backorder.colNeededBy'),
      mono: true,
      align: 'right',
      width: '8rem',
      render: (row) => dateText(row.neededBy) ?? <span className="text-text-faint">—</span>,
    },
    {
      /* Amallar o'ngda YOPISHIB turadi: ustunlar ko'p va gorizontal
         siljiganda tugmalar ko'zdan qochardi. */
      key: 'actions',
      header: t('backorder.colActions'),
      align: 'right',
      width: '13rem',
      sticky: 'right',
      render: (row) => {
        const actions = actionsFor(row);
        if (
          actions.onAccept === undefined &&
          actions.onFulfil === undefined &&
          actions.onCancel === undefined
        ) {
          return <span className="text-text-faint">—</span>;
        }
        return (
          <span className="flex items-center justify-end gap-1.5">
            {actions.onCancel === undefined ? null : (
              <Button variant="ghost" size="sm" onClick={() => actions.onCancel?.(row)}>
                <X size={14} aria-hidden />
                {t('backorder.withdraw')}
              </Button>
            )}
            {actions.onAccept === undefined ? null : (
              <Button size="sm" onClick={() => actions.onAccept?.(row)}>
                {t('backorder.decide')}
              </Button>
            )}
            {actions.onFulfil === undefined ? null : (
              <Button size="sm" onClick={() => actions.onFulfil?.(row)}>
                <PackageCheck size={14} aria-hidden />
                {t('backorder.fulfil')}
              </Button>
            )}
          </span>
        );
      },
    },
  ];

  return (
    <Table
      caption={caption}
      columns={columns}
      rows={[...rows]}
      rowKey={(row) => row.id}
      density="compact"
    />
  );
};
